// Tuning constants for the arcade car model. Values are metres, seconds,
// and radians unless noted. These are starting points; Task 13 tunes by feel.

export interface CarTuning {
  accel: number; // m/s^2 at full throttle
  brake: number; // m/s^2
  reverseAccel: number;
  maxSpeed: number; // m/s
  maxReverse: number;
  drag: number; // per second, applied to forward speed
  turnRate: number; // rad/s at full steer, at or above turnFullSpeed
  turnFullSpeed: number; // speed where turn rate reaches its max
  gripHigh: number; // fraction of lateral velocity kept per tick at full grip
  gripLow: number; // fraction of lateral velocity kept per tick at full drift
  driftThreshold: number; // fraction of maxSpeed above which hard steering starts to drift
  radius: number; // collision radius in metres
}

export const SPORTS_CAR: CarTuning = {
  accel: 14,
  brake: 22,
  reverseAccel: 6,
  maxSpeed: 16,
  maxReverse: 4,
  drag: 0.6,
  turnRate: 3.2,
  turnFullSpeed: 6,
  gripHigh: 0.8,
  gripLow: 0.955,
  driftThreshold: 0.55,
  radius: 0.45,
};

// Multipliers applied by the surface a car is standing on. 1 is neutral.
export interface SurfaceMod {
  speed: number;
  grip: number;
}

// Tuning for the centreline-following AI driver (src/sim/ai.ts). speedFrac
// and jitter are the difficulty knobs proper (harder = drives closer to top
// speed, with less steering noise); lookahead, steerGain and brakeCurvature
// are the driving-envelope knobs, retuned per difficulty against the actual
// KITCHEN_TABLE track (see task-7-report.md) so each difficulty completes a
// lap without clipping a wall. laneOffset is not difficulty-specific: it is
// set per car from its starting slot (see aiInput's caller), so AI_DIFFICULTY
// carries 0 here as a placeholder that every caller is expected to override.
export interface AiParams {
  lookahead: number; // metres ahead along the centreline the AI steers toward
  steerGain: number; // proportional gain from heading error to steer input
  speedFrac: number; // fraction of (rubber-banded) max speed targeted on straights
  jitter: number; // amplitude of random steering noise, in steer units
  brakeCurvature: number; // how strongly upcoming curvature lowers the speed target
  laneOffset: number; // metres, lateral offset from centreline (right positive)
}

export const AI_DIFFICULTY: Record<'easy' | 'normal' | 'hard', AiParams> = {
  easy: { lookahead: 15, steerGain: 6.0, speedFrac: 0.72, jitter: 0.15, brakeCurvature: 0.25, laneOffset: 0 },
  normal: { lookahead: 15, steerGain: 6.0, speedFrac: 0.85, jitter: 0.08, brakeCurvature: 0.3, laneOffset: 0 },
  hard: { lookahead: 16, steerGain: 5.0, speedFrac: 0.97, jitter: 0.03, brakeCurvature: 0.15, laneOffset: 0 },
};
